/**
 * Előfizetés és számlázás oldal — teljes implementáció
 */
import { useState, useEffect } from 'react';
import { get, post, del } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Modal } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useFeatureContext } from '../../contexts/FeatureContext';

function formatHuf(filler: number): string { return Math.round(filler / 100).toLocaleString('hu-HU'); }
function formatHufGross(filler: number): string { return Math.round(filler * 1.27 / 100).toLocaleString('hu-HU'); }
function formatDate(iso: string | null | undefined): string { return iso ? new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'; }
const MIN_PRORATED_FILLER = 200000; // 2 000 Ft nettó alatt nem terheljük előre

const STATUS_BADGE: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  pre_trial: { label: 'Próba előtt', variant: 'default' },
  trial: { label: 'Próba', variant: 'info' },
  active: { label: 'Aktív', variant: 'success' },
  past_due: { label: 'Lejárt fizetés', variant: 'warning' },
  grace: { label: 'Türelmi idő', variant: 'warning' },
  read_only: { label: 'Csak olvasás', variant: 'error' },
  locked: { label: 'Zárolva', variant: 'error' },
  cancelled: { label: 'Lemondva', variant: 'default' },
  ended: { label: 'Megszűnt', variant: 'default' },
};

const PLAN_INFO: Record<string, { name: string; features: string[] }> = {
  start: {
    name: 'Start',
    features: ['Partnerek', 'Cikkek + 1 árlista', 'Számlák + NAV', 'Bejövő számlák', 'Kintlévőség', 'Dokumentumtár', '30 számla/hó', '1 felhasználó'],
  },
  standard: {
    name: 'Standard',
    features: ['Minden Start funkció', 'Megrendelések + Szállítólevelek', 'Projektek', '5 ismétlődő számla/hó', '2 felhasználó + 1 könyvelő'],
  },
  pro: {
    name: 'Pro',
    features: ['Minden Standard funkció', 'Időnaplózás', 'Beszerzés', 'Költségallokáció', '100 ismétlődő számla/hó', '3+ felhasználó'],
  },
};

// Árak fillérben (plans.ts-sel megegyező)
const PLAN_PRICES: Record<string, { monthly: number; yearly: number }> = {
  start:    { monthly: 299000,  yearly: 2870400 },
  standard: { monthly: 599000,  yearly: 5750400 },
  pro:      { monthly: 1299000, yearly: 12470400 },
};

const ADDON_INFO: Record<string, { name: string; priceMonthly: number; description: string }> = {
  // Csomagban szereplő alapmodulok (is_included=true)
  partners:                    { name: 'Partnerek',                             priceMonthly: 0,      description: 'Partner- és ügyfélnyilvántartás' },
  items_pricing:               { name: 'Cikktörzs + Árazás',                   priceMonthly: 0,      description: 'Cikkek, árlisták, készlet' },
  invoices_basic:              { name: 'Számlázás (NAV)',                       priceMonthly: 0,      description: 'Kiadott számlák + NAV Online integráció' },
  incoming_receivables:        { name: 'Bejövő számlák + Kintlévőség',         priceMonthly: 0,      description: 'Szállítói számlák és kintlévőség-kezelés' },
  documents_search:            { name: 'Dokumentumtár + Keresés',              priceMonthly: 0,      description: 'Fájlkezelés és globális keresés' },
  // Kiegészítő modulok
  sales_flow:                  { name: 'Megrendelések + Szállítólevelek',      priceMonthly: 50000,  description: 'Értékesítési folyamat teljes kezelése' },
  projects_tasks:              { name: 'Projektek + Feladatok',                priceMonthly: 199000, description: 'Projekt- és feladatmenedzsment' },
  time_tracking:               { name: 'Időnaplózás',                          priceMonthly: 99000,  description: 'Munkaidő nyilvántartás és számlázás' },
  recurring_invoices_unlimited:{ name: 'Ismétlődő számlák (korlátlan)',        priceMonthly: 50000,  description: 'Korlátlan ismétlődő számla' },
  procurement:                 { name: 'Beszerzés',                            priceMonthly: 149000, description: 'RFQ, megrendelés, bevételezés' },
  cost_allocation:             { name: 'Költségallokáció',                     priceMonthly: 299000, description: 'Projekt-szintű költségkönyvelés (min. Standard)' },
};

const PAYMENT_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Fizetésre vár', color: '#f59e0b' },
  paid:    { label: 'Fizetve', color: '#16a34a' },
  failed:  { label: 'Sikertelen', color: '#dc2626' },
  refunded:{ label: 'Visszatérítve', color: '#6b7280' },
};

interface AddonStatus {
  addonType: 'KONFIG_OLCSO' | 'KONFIG_PRO' | 'IDOPONT_BASIC' | 'IDOPONT_PRO';
  group: 'konfig' | 'idopont';
  name: string;
  description: string;
  monthly: { priceFiller: number; discountPct: number };
  yearly: { priceFiller: number; discountPct: number };
  isActive: boolean;
  activeBillingCycle: 'monthly' | 'yearly' | null;
  periodEnd: string | null;
  proDiscount10: boolean;
}

export default function BillingPage() {
  const { toast } = useToast();
  const { refresh: refreshFeatures } = useFeatureContext();
  const [data, setData] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [addons, setAddons] = useState<AddonStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState<AddonStatus | null>(null);
  const [addonCycle, setAddonCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [modulePreview, setModulePreview] = useState<{ moduleCode: string; proRatedFiller: number } | null>(null);
  const [cyclePreview, setCyclePreview] = useState<any>(null);
  const [savingPlan, setSavingPlan] = useState('');
  const [savingModule, setSavingModule] = useState('');
  const [savingCycle, setSavingCycle] = useState(false);
  const [savingAddon, setSavingAddon] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [sub, inv, addonList] = await Promise.all([
        get<any>('/subscriptions/current').catch(() => null),
        get<any[]>('/subscriptions/invoices').catch(() => []),
        get<AddonStatus[]>('/addons').catch(() => []),
      ]);
      setData(sub);
      setInvoices(Array.isArray(inv) ? inv : []);
      setAddons(Array.isArray(addonList) ? addonList : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>;
  if (!data) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Nincs aktív előfizetés.</div>;

  const sub = data.subscription || {};
  const status = STATUS_BADGE[sub.status] || { label: sub.status || '—', variant: 'default' as const };
  const planName = PLAN_INFO[sub.plan_code]?.name || sub.plan_code || '—';
  const modules: any[] = data.modules || [];
  const seats: any[] = data.seats || [];
  const trialDaysUsed = sub.trial_started_at ? Math.floor((Date.now() - new Date(sub.trial_started_at).getTime()) / 86400000) : 0;
  const trialDaysLeft = sub.trial_ends_at ? Math.max(0, Math.ceil((new Date(sub.trial_ends_at).getTime() - Date.now()) / 86400000)) : 0;

  // Active addon module codes
  const activeModuleCodes = new Set(modules.filter(m => !m.ends_at || new Date(m.ends_at) > new Date()).map(m => m.module_code));

  // Addons that can still be added (not already active/included)
  const addableAddons = Object.entries(ADDON_INFO).filter(([code]) => !activeModuleCodes.has(code));

  async function handleChangePlan(plan: string) {
    setSavingPlan(plan);
    try {
      await post('/subscriptions/change-plan', { newPlan: plan, confirm: true });
      toast('Csomag módosítva.', 'success');
      setShowPlanModal(false);
      await loadData();
      refreshFeatures();
    } catch (e: any) {
      toast(e.message || 'Hiba a csomag módosításakor.', 'error');
    } finally {
      setSavingPlan('');
    }
  }

  async function handleAddModulePreview(moduleCode: string) {
    setSavingModule(moduleCode);
    try {
      const preview = await post<any>('/subscriptions/add-module', { moduleCode, confirm: false });
      const proRatedFiller = preview?.preview?.proRatedAmount ?? 0;
      setModulePreview({ moduleCode, proRatedFiller });
      setShowAddModuleModal(false);
    } catch (e: any) {
      toast(e.message || 'Hiba.', 'error');
    } finally {
      setSavingModule('');
    }
  }

  async function handleAddModuleConfirm() {
    if (!modulePreview) return;
    setSavingModule(modulePreview.moduleCode);
    try {
      await post('/subscriptions/add-module', { moduleCode: modulePreview.moduleCode, confirm: true });
      toast('Modul aktiválva.', 'success');
      setModulePreview(null);
      await loadData();
      refreshFeatures();
    } catch (e: any) {
      toast(e.message || 'Hiba a modul aktiválásakor.', 'error');
    } finally {
      setSavingModule('');
    }
  }

  async function handleRemoveModule(moduleCode: string) {
    try {
      await post('/subscriptions/remove-module', { moduleCode });
      toast('Modul lemondva — a periódus végéig aktív marad.', 'success');
      await loadData();
    } catch (e: any) {
      toast(e.message || 'Hiba.', 'error');
    }
  }

  async function handleActivateAddon(addonType: string, billingCycle: 'monthly' | 'yearly') {
    setSavingAddon(addonType);
    try {
      await post('/addons/activate', { addonType, billingCycle });
      toast('Modul aktiválva.', 'success');
      setShowAddonModal(null);
      await loadData();
      refreshFeatures();
    } catch (e: any) {
      toast(e.message || 'Hiba a modul aktiválásakor.', 'error');
    } finally {
      setSavingAddon('');
    }
  }

  async function handleDeactivateAddon(addonType: string) {
    setSavingAddon(addonType);
    try {
      await del(`/addons/${addonType}`);
      toast('Modul lemondva.', 'success');
      await loadData();
      refreshFeatures();
    } catch (e: any) {
      toast(e.message || 'Hiba.', 'error');
    } finally {
      setSavingAddon('');
    }
  }

  async function previewCycleChange(newCycle: string) {
    try {
      const preview = await post<any>('/subscriptions/change-billing-cycle', { newCycle });
      setCyclePreview({ ...preview, newCycle });
      setShowCycleModal(true);
    } catch (e: any) {
      toast(e.message || 'Hiba.', 'error');
    }
  }

  async function confirmCycleChange() {
    if (!cyclePreview?.newCycle) return;
    setSavingCycle(true);
    try {
      await post('/subscriptions/change-billing-cycle', { newCycle: cyclePreview.newCycle, confirm: true });
      toast('Számlázási ciklus módosítva.', 'success');
      setShowCycleModal(false);
      setCyclePreview(null);
      await loadData();
    } catch (e: any) {
      toast(e.message || 'Hiba.', 'error');
    } finally {
      setSavingCycle(false);
    }
  }

  async function handleSetupPaymentMethod() {
    try {
      const d = await post<any>('/subscriptions/setup-payment-method', {});
      if (d.gatewayUrl) window.location.href = d.gatewayUrl;
    } catch (e: any) {
      toast(e.message || 'Hiba a fizetési mód beállításakor.', 'error');
    }
  }

  async function handleCancelPaymentMethod() {
    try {
      await post('/subscriptions/cancel-payment-method', {});
      toast('Előfizetés lemondva.', 'success');
      setShowCancelModal(false);
      await loadData();
      refreshFeatures();
    } catch (e: any) {
      toast(e.message || 'Hiba.', 'error');
    }
  }

  return (
    <div>
      <PageHeader title="Előfizetés és számlázás" description={`${planName} csomag`} />

      {/* Jelenlegi csomag */}
      <Card style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>Jelenlegi csomag</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--navy)' }}>{planName}</div>
            <div style={{ fontSize: '14px', color: 'var(--slate)', marginTop: '4px' }}>
              {formatHuf(data.total_monthly_filler || 0)} Ft + ÁFA / {sub.billing_cycle === 'yearly' ? 'év' : 'hó'}
              <span style={{ marginLeft: 8, color: 'var(--slate)', fontSize: 12 }}>
                ({formatHufGross(data.total_monthly_filler || 0)} Ft bruttó)
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <Badge variant={status.variant}>{status.label}</Badge>
            <div style={{ fontSize: '12px', color: 'var(--slate)' }}>
              {sub.billing_cycle === 'yearly' ? '📅 Éves számlázás' : '📅 Havi számlázás'}
            </div>
          </div>
        </div>

        {sub.status === 'trial' && (
          <div style={{ marginTop: '16px', padding: '14px 16px', background: 'rgba(244,123,32,0.08)', borderRadius: '10px' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>Próba: {trialDaysUsed} / 30 nap ({trialDaysLeft} nap maradt)</div>
            <div style={{ marginTop: '8px', height: '6px', background: 'var(--line)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(100, trialDaysUsed / 30 * 100)}%`, background: 'var(--orange)', borderRadius: '4px' }} />
            </div>
          </div>
        )}

        <div style={{ marginTop: '14px', fontSize: '13px', color: 'var(--slate)', display: 'grid', gap: '4px' }}>
          <div>Periódus: {formatDate(sub.current_period_starts_at)} – {formatDate(sub.current_period_ends_at)}</div>
          {data.next_billing_at && <div>Következő fizetés: {formatDate(data.next_billing_at)} • {formatHuf(data.next_billing_amount_filler || 0)} Ft + ÁFA ({formatHufGross(data.next_billing_amount_filler || 0)} Ft bruttó)</div>}
        </div>

        <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <Button variant="secondary" onClick={() => setShowPlanModal(true)}>Csomag módosítása</Button>
          {sub.billing_cycle === 'monthly' ? (
            <Button variant="ghost" onClick={() => previewCycleChange('yearly')}>Váltás éves számlázásra</Button>
          ) : (
            <Button variant="ghost" onClick={() => previewCycleChange('monthly')}>Váltás havi számlázásra</Button>
          )}
          <Button variant="ghost" style={{ color: 'var(--red, #dc2626)' }} onClick={() => setShowCancelModal(true)}>Lemondás</Button>
        </div>
      </Card>

      {/* Modulok */}
      <Card style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>Modulok</div>
          {addableAddons.length > 0 && (
            <Button size="sm" onClick={() => setShowAddModuleModal(true)}>+ Modul hozzáadása</Button>
          )}
        </div>

        {/* Előfizetéses modulok */}
        {modules.length === 0 ? (
          <div style={{ padding: '12px', color: 'var(--slate)', fontSize: '13px' }}>Nincs kiegészítő modul — csak az alapcsomag funkciói aktívak.</div>
        ) : (
          <div style={{ display: 'grid', gap: '8px', marginBottom: addons.length > 0 ? '16px' : 0 }}>
            {modules.map((m: any) => {
              const info = ADDON_INFO[m.module_code];
              const ending = m.ends_at && new Date(m.ends_at) <= new Date();
              return (
                <div key={m.id || m.module_code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 'var(--r)', background: 'var(--mist)', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: ending ? 'var(--slate)' : 'var(--navy)' }}>
                      {info?.name || m.module_code.replace(/_/g, ' ')}
                      {ending && <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--red, #dc2626)' }}>• Lemondva</span>}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px' }}>
                      {m.is_included ? 'Csomagban szerepel' : `+${formatHuf(Number(m.price_filler))} Ft + ÁFA / hó`}
                      {m.ends_at && !ending && ` · Aktív: ${formatDate(m.ends_at)}-ig`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Badge variant={m.is_included ? 'success' : 'info'}>{m.is_included ? 'Alap' : 'Kiegészítő'}</Badge>
                    {!m.is_included && !m.ends_at && (
                      <button onClick={() => handleRemoveModule(m.module_code)} style={{ fontSize: '11px', color: 'var(--slate)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                        Lemondás
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Addon szekciók — Konfigurátor + Időpontfoglaló */}
        {(['konfig', 'idopont'] as const).map(group => {
          const groupAddons = addons.filter(a => a.group === group);
          const groupLabel = group === 'konfig' ? 'Konfigurátor modulok' : 'Időpontfoglaló modulok';
          const proDisc = groupAddons[0]?.proDiscount10 ?? false;
          return (
            <div key={group} style={{ borderTop: '1px solid var(--line)', marginTop: '4px', paddingTop: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '10px' }}>{groupLabel}</div>
              {proDisc && group === 'konfig' && (
                <div style={{ marginBottom: '10px', padding: '9px 12px', background: 'rgba(22,163,74,0.08)', borderRadius: 'var(--r)', fontSize: '12px', color: '#166534', fontWeight: 600 }}>
                  Pro-előfizetőként 10% kedvezménnyel veheted igénybe a konfigurátor modulokat.
                </div>
              )}
              {groupAddons.length === 0 ? (
                <div style={{ padding: '10px 12px', color: 'var(--slate)', fontSize: '13px', background: 'var(--mist)', borderRadius: 'var(--r)' }}>
                  Betöltés sikertelen. Kérjük, frissítsd az oldalt.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {groupAddons.map(addon => {
                    const monthlyNet = addon.proDiscount10 ? Math.round(addon.monthly.priceFiller * 0.9) : addon.monthly.priceFiller;
                    const yearlyNet  = addon.proDiscount10 ? Math.round(addon.yearly.priceFiller  * 0.9) : addon.yearly.priceFiller;
                    return (
                      <div key={addon.addonType} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 'var(--r)', border: addon.isActive ? '2px solid var(--orange)' : '1px solid var(--line)', background: addon.isActive ? 'rgba(244,123,32,0.04)' : 'var(--mist)', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                            <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--navy)' }}>{addon.name}</span>
                            {addon.isActive && <Badge variant="success">Aktív</Badge>}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--slate)' }}>
                            {formatHuf(monthlyNet)} Ft + ÁFA / hó{addon.proDiscount10 && <span style={{ marginLeft: 4, color: '#16a34a', fontWeight: 600 }}>(-10%)</span>}
                            {' · '}{formatHuf(yearlyNet)} Ft + ÁFA / év{addon.yearly.discountPct > 0 && <span style={{ marginLeft: 4, color: 'var(--orange)' }}>(-{Math.round(addon.yearly.discountPct)}%)</span>}
                            {addon.isActive && addon.activeBillingCycle && <span style={{ marginLeft: 6 }}>· {addon.activeBillingCycle === 'monthly' ? 'Havi számlázás' : 'Éves számlázás'}</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: 2 }}>{addon.description}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {addon.isActive ? (
                            <button onClick={() => handleDeactivateAddon(addon.addonType)} disabled={savingAddon === addon.addonType} style={{ fontSize: '11px', color: 'var(--slate)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                              {savingAddon === addon.addonType ? 'Lemondás...' : 'Lemondás'}
                            </button>
                          ) : (
                            <Button size="sm" onClick={() => { setShowAddonModal(addon); setAddonCycle('monthly'); }} disabled={savingAddon === addon.addonType}>
                              Aktiválás
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </Card>

      {/* Felhasználói férőhelyek */}
      <Card style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>Felhasználói férőhelyek ({seats.length})</div>
          <Button size="sm" variant="ghost" onClick={async () => {
            try { await post('/subscriptions/add-seat', { seatType: 'regular', confirm: true }); toast('Férőhely hozzáadva.', 'success'); await loadData(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
          }}>+ Férőhely</Button>
        </div>
        {seats.length === 0 ? (
          <div style={{ padding: '12px', color: 'var(--slate)', fontSize: '13px' }}>Nincs seat adat.</div>
        ) : (
          <div style={{ display: 'grid', gap: '6px' }}>
            {seats.map((s: any, i: number) => (
              <div key={s.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 'var(--r)', background: 'var(--mist)' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', textTransform: 'capitalize' }}>{s.seat_type}</div>
                  <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{s.is_included ? 'Alapcsomag' : `+${formatHuf(Number(s.price_filler || 0))} Ft + ÁFA / hó`}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Badge variant={s.assigned_user_id ? 'success' : 'default'}>{s.assigned_user_id ? 'Hozzárendelve' : 'Szabad'}</Badge>
                  {!s.is_included && !s.assigned_user_id && (
                    <button onClick={async () => {
                      try { await post('/subscriptions/remove-seat', { seatId: s.id }); toast('Férőhely eltávolítva.', 'success'); await loadData(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                    }} style={{ fontSize: '11px', color: 'var(--slate)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      Eltávolítás
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Fizetési mód */}
      <Card style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)', marginBottom: '12px' }}>Fizetési mód</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '13px', color: 'var(--navy)' }}>
              {sub.payment_method_id ? '✅ Barion kártya beállítva' : '⚠️ Nincs fizetési mód beállítva'}
            </div>
            {!sub.payment_method_id && (
              <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '4px' }}>
                Kártyaadatok megadása szükséges az előfizetés automatikus megújításához.
              </div>
            )}
          </div>
          {sub.payment_method_id ? (
            <Button size="sm" variant="ghost" onClick={() => setShowCancelModal(true)}>Kártya eltávolítása</Button>
          ) : (
            <Button size="sm" onClick={handleSetupPaymentMethod}>Kártya beállítása</Button>
          )}
        </div>
      </Card>

      {/* Számlázási előzmények */}
      <Card style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)', marginBottom: '12px' }}>Számlázási előzmények</div>
        {invoices.length === 0 ? (
          <div style={{ padding: '12px', color: 'var(--slate)', fontSize: '13px' }}>Nincs korábbi számla.</div>
        ) : (
          <div style={{ display: 'grid', gap: '6px' }}>
            {invoices.map((inv: any) => {
              const ps = PAYMENT_STATUS[inv.payment_status] || { label: inv.payment_status, color: 'var(--slate)' };
              return (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 'var(--r)', background: 'var(--mist)', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>
                      {formatDate(inv.period_starts_at)} – {formatDate(inv.period_ends_at)}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--slate)' }}>Kiállítva: {formatDate(inv.created_at)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '14px' }}>{formatHuf(Number(inv.total_filler))} Ft</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: ps.color }}>{ps.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Csomag módosítás modal */}
      <Modal isOpen={showPlanModal} onClose={() => setShowPlanModal(false)} title="Csomag módosítása">
        <div style={{ display: 'grid', gap: '10px' }}>
          {(['start', 'standard', 'pro'] as const).map(plan => {
            const info = PLAN_INFO[plan];
            const prices = PLAN_PRICES[plan];
            const isCurrent = plan === sub.plan_code;
            return (
              <button key={plan} onClick={() => !isCurrent && handleChangePlan(plan)} disabled={isCurrent || !!savingPlan} style={{
                textAlign: 'left', padding: '14px 16px', borderRadius: 'var(--r)',
                border: isCurrent ? '2px solid var(--orange)' : '1px solid var(--line)',
                background: isCurrent ? 'rgba(244,123,32,0.06)' : 'var(--white)',
                cursor: isCurrent ? 'default' : 'pointer', opacity: savingPlan && savingPlan !== plan ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '15px' }}>{info.name}</div>
                  <div style={{ fontSize: '13px', color: 'var(--slate)' }}>
                    {formatHuf(prices.monthly)} Ft + ÁFA / hó
                    {sub.billing_cycle === 'yearly' && ` · ${formatHuf(prices.yearly)} Ft + ÁFA / év`}
                  </div>
                </div>
                <ul style={{ margin: '8px 0 0', padding: '0 0 0 16px', fontSize: '12px', color: 'var(--slate)' }}>
                  {info.features.slice(0, 4).map(f => <li key={f}>{f}</li>)}
                </ul>
                {isCurrent && <div style={{ marginTop: '6px', fontSize: '11px', fontWeight: 700, color: 'var(--orange)' }}>Jelenlegi csomag</div>}
                {savingPlan === plan && <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--slate)' }}>Módosítás...</div>}
              </button>
            );
          })}
        </div>
        <Button variant="ghost" fullWidth style={{ marginTop: '12px' }} onClick={() => setShowPlanModal(false)}>Mégse</Button>
      </Modal>

      {/* Modul hozzáadása modal */}
      <Modal isOpen={showAddModuleModal} onClose={() => setShowAddModuleModal(false)} title="Modul hozzáadása">
        <div style={{ display: 'grid', gap: '10px' }}>
          {addableAddons.map(([code, info]) => (
            <button key={code} onClick={() => handleAddModulePreview(code)} disabled={!!savingModule} style={{
              textAlign: 'left', padding: '14px 16px', borderRadius: 'var(--r)',
              border: '1px solid var(--line)', background: 'var(--white)', cursor: 'pointer',
              opacity: savingModule && savingModule !== code ? 0.6 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '14px' }}>{info.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 600 }}>+{formatHuf(info.priceMonthly)} Ft + ÁFA / hó</div>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '4px' }}>{info.description}</div>
              {savingModule === code && <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>Lekérdezés...</div>}
            </button>
          ))}
        </div>
        <Button variant="ghost" fullWidth style={{ marginTop: '12px' }} onClick={() => setShowAddModuleModal(false)}>Mégse</Button>
      </Modal>

      {/* Modul aktiválás megerősítés modal */}
      <Modal isOpen={!!modulePreview} onClose={() => setModulePreview(null)} title="Modul aktiválása">
        {modulePreview && (() => {
          const info = ADDON_INFO[modulePreview.moduleCode];
          const proNetto = modulePreview.proRatedFiller;
          const proBrutto = Math.round(proNetto * 1.27);
          const isSmall = proNetto < MIN_PRORATED_FILLER;
          return (
            <div style={{ display: 'grid', gap: '14px' }}>
              <div style={{ padding: '14px 16px', background: 'var(--mist)', borderRadius: 'var(--r)' }}>
                <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--navy)' }}>{info?.name || modulePreview.moduleCode}</div>
                <div style={{ fontSize: '13px', color: 'var(--slate)', marginTop: '4px' }}>
                  {formatHuf(info?.priceMonthly || 0)} Ft + ÁFA / hó
                  <span style={{ marginLeft: 8, color: 'var(--slate)', fontSize: 12 }}>({formatHufGross(info?.priceMonthly || 0)} Ft bruttó)</span>
                </div>
              </div>

              {proNetto > 0 ? (
                <div style={{ padding: '12px 14px', borderRadius: 'var(--r)', border: `1.5px solid ${isSmall ? 'var(--line)' : '#f59e0b'}`, background: isSmall ? 'var(--mist)' : '#fffbeb' }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: isSmall ? 'var(--navy)' : '#92400e', marginBottom: 4 }}>
                    {isSmall ? 'Arányos díj' : '⚠️ Előre fizetendő arányos díj'}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--slate)', lineHeight: 1.6 }}>
                    A következő számlázási időszakig hátralévő napokra arányos díj:{' '}
                    <strong>{formatHuf(proNetto)} Ft + ÁFA = {formatHufGross(proNetto).toLocaleString('hu-HU')} Ft bruttó</strong>.
                  </div>
                  <div style={{ fontSize: '12px', marginTop: 6, color: isSmall ? 'var(--slate)' : '#92400e' }}>
                    {isSmall
                      ? 'Az összeg 2 000 Ft alatt van — a következő havi/éves számlával együtt kerül felszámolásra.'
                      : 'Ez az összeg a következő számlával kerül felszámolásra (a következő esedékességkor).'}
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: '13px', color: 'var(--slate)' }}>A modul az aktuális periódus elejétől aktív — külön arányos díj nincs.</p>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', marginTop: 4 }}>
                <Button variant="ghost" onClick={() => setModulePreview(null)}>Mégse</Button>
                <Button onClick={handleAddModuleConfirm} disabled={!!savingModule}>
                  {savingModule ? 'Aktiválás...' : 'Modul aktiválása'}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Számlázási ciklus módosítás modal */}
      <Modal isOpen={showCycleModal} onClose={() => { setShowCycleModal(false); setCyclePreview(null); }} title="Számlázási ciklus módosítása">
        {cyclePreview && (
          <div style={{ display: 'grid', gap: '14px' }}>
            {cyclePreview.preview ? (
              <>
                <p style={{ fontSize: '14px', color: 'var(--slate)' }}>
                  {cyclePreview.newCycle === 'yearly'
                    ? `Éves számlázásra váltva ${formatHuf(cyclePreview.preview.newPrice / 100)} Ft-os éves díjjal. Megtakarítás: ~${formatHuf(cyclePreview.preview.savings / 100)} Ft/év.`
                    : cyclePreview.preview.message}
                </p>
                {cyclePreview.preview.effectiveFrom && (
                  <p style={{ fontSize: '13px', color: 'var(--slate)' }}>Érvényes: {formatDate(cyclePreview.preview.effectiveFrom)}</p>
                )}
                {cyclePreview.newCycle === 'yearly' && (
                  <div style={{ display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
                    <Button variant="ghost" onClick={() => { setShowCycleModal(false); setCyclePreview(null); }}>Mégse</Button>
                    <Button onClick={confirmCycleChange} disabled={savingCycle}>{savingCycle ? 'Módosítás...' : 'Megerősítés'}</Button>
                  </div>
                )}
                {cyclePreview.newCycle !== 'yearly' && (
                  <Button variant="ghost" onClick={() => { setShowCycleModal(false); setCyclePreview(null); }}>Bezárás</Button>
                )}
              </>
            ) : (
              <>
                <p style={{ fontSize: '14px', color: 'var(--slate)' }}>{cyclePreview.message || 'A ciklus módosítva.'}</p>
                <Button onClick={() => { setShowCycleModal(false); setCyclePreview(null); }}>Bezárás</Button>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Modul aktiválás modal */}
      <Modal isOpen={!!showAddonModal} onClose={() => setShowAddonModal(null)} title="Modul aktiválása">
        {showAddonModal && (() => {
          const addon = showAddonModal;
          const monthlyNet = addon.proDiscount10 ? Math.round(addon.monthly.priceFiller * 0.9) : addon.monthly.priceFiller;
          const yearlyNet  = addon.proDiscount10 ? Math.round(addon.yearly.priceFiller  * 0.9) : addon.yearly.priceFiller;
          const chosenPrice = addonCycle === 'monthly' ? monthlyNet : yearlyNet;
          return (
            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ padding: '14px 16px', background: 'var(--mist)', borderRadius: 'var(--r)' }}>
                <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--navy)', marginBottom: 4 }}>{addon.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--slate)' }}>{addon.description}</div>
              </div>

              {addon.proDiscount10 && (
                <div style={{ padding: '10px 14px', background: 'rgba(22,163,74,0.08)', borderRadius: 'var(--r)', fontSize: '13px', color: '#166534', fontWeight: 600 }}>
                  Pro kedvezmény: 10% — automatikusan alkalmazva.
                </div>
              )}

              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', marginBottom: '8px' }}>Számlázási ciklus</div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {(['monthly', 'yearly'] as const).map(cycle => (
                    <button key={cycle} onClick={() => setAddonCycle(cycle)} style={{
                      textAlign: 'left', padding: '12px 14px', borderRadius: 'var(--r)',
                      border: addonCycle === cycle ? '2px solid var(--orange)' : '1px solid var(--line)',
                      background: addonCycle === cycle ? 'rgba(244,123,32,0.05)' : 'var(--white)',
                      cursor: 'pointer',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)' }}>
                          {cycle === 'monthly' ? 'Havi számlázás' : 'Éves számlázás'}
                        </span>
                        <span style={{ fontWeight: 700, color: 'var(--orange)', fontSize: '14px' }}>
                          {formatHuf(cycle === 'monthly' ? monthlyNet : yearlyNet)} Ft + ÁFA / {cycle === 'monthly' ? 'hó' : 'év'}
                        </span>
                      </div>
                      {cycle === 'yearly' && addon.yearly.discountPct > 0 && (
                        <div style={{ fontSize: '11px', color: 'var(--orange)', marginTop: 4 }}>
                          ~{Math.round(addon.yearly.discountPct)}% megtakarítás az éves díjon
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ padding: '12px 14px', borderRadius: 'var(--r)', background: 'var(--mist)', fontSize: '13px', color: 'var(--slate)' }}>
                Fizetendő: <strong>{formatHuf(chosenPrice)} Ft + ÁFA</strong> ({formatHufGross(chosenPrice)} Ft bruttó) / {addonCycle === 'monthly' ? 'hó' : 'év'}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px' }}>
                <Button variant="ghost" onClick={() => setShowAddonModal(null)}>Mégse</Button>
                <Button onClick={() => handleActivateAddon(addon.addonType, addonCycle)} disabled={!!savingAddon}>
                  {savingAddon ? 'Aktiválás...' : 'Modul aktiválása'}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Lemondás modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Előfizetés lemondása">
        <div style={{ display: 'grid', gap: '14px' }}>
          <p style={{ fontSize: '14px', color: 'var(--slate)', lineHeight: 1.6 }}>
            Biztosan lemondod az előfizetésed? A jelenlegi periódus végéig ({formatDate(sub.current_period_ends_at)}) továbbra is hozzáférsz a rendszerhez.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px' }}>
            <Button variant="ghost" onClick={() => setShowCancelModal(false)}>Mégse</Button>
            <Button variant="danger" onClick={handleCancelPaymentMethod}>Lemondás megerősítése</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
